import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SendIcon from "@mui/icons-material/Send";
import { useQueryClient } from "react-query";

import { API_URL } from "../../config";
import { invalidateNavigationSummary } from "../../hooks/useNavigationSummary";
import { LeonTransferEditDialog } from "../leon/LeonTransferEditDialog";
import { formatDateTime } from "../../utils";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "—";
  return formatDateTime(date);
};

const toAbsoluteApiPath = (pathValue?: string | null) => {
  if (!pathValue) return null;

  const normalizedPath = `${pathValue}`.trim();
  if (!normalizedPath.length) {
    return null;
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return normalizedPath;
  }

  const cleanApiBase = `${API_URL}`.replace(/\/+$/, "");
  const baseHasApiSuffix = /\/api$/i.test(cleanApiBase);
  let pathWithSlash = normalizedPath.startsWith("/")
    ? normalizedPath
    : `/${normalizedPath}`;
  pathWithSlash = pathWithSlash.replace(/^\/(?:api\/)+api(\/|$)/i, "/api$1");
  pathWithSlash = pathWithSlash.replace(/^\/api\/api(\/|$)/i, "/api$1");

  if (baseHasApiSuffix && /^\/api(?:\/|$)/i.test(pathWithSlash)) {
    pathWithSlash = pathWithSlash.replace(/^\/api/i, "");
  }

  return `${cleanApiBase}${pathWithSlash}`;
};

const parseRecipients = (value: string) =>
  Array.from(
    new Set(
      `${value || ""}`
        .split(/[\n,;]+/)
        .map((entry) => entry.trim().toLowerCase())
        .filter(Boolean),
    ),
  );

const isValidRecipient = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getFileNameFromPath = (value?: string | null) => {
  if (!value) return null;
  const parts = `${value}`.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : null;
};

const formatBoolLabel = (value?: boolean | null) => {
  if (value === null || value === undefined) return "—";
  return value ? "Yes" : "No";
};

const formatApproachDayLabel = (value?: boolean | null) => {
  if (value === null || value === undefined) return "—";
  return value ? "Day" : "Night";
};

const formatNumberLabel = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${value}`;
};

const formatCrewOptionLabel = (
  crewOptions: LeonJourneyLogCrewOption[] = [],
  crewId?: number | null,
) => {
  if (!crewId) return "—";
  const crew = crewOptions.find((entry) => entry.id === crewId);
  if (!crew) return `Crew ${crewId}`;
  return [crew.tlc, crew.rank, crew.position].filter(Boolean).join(" · ") || `Crew ${crewId}`;
};

const formatApproachSummary = (rows: LeonJourneyLogApproachRow[] = []) => {
  if (!rows.length) return ["No approach rows"];
  return rows.map((row) => {
    const values = [
      row.type || "Type —",
      `Successful ${formatBoolLabel(row.successful)}`,
      `Period ${formatApproachDayLabel(row.day)}`,
      `EFVS ${formatBoolLabel(row.EFVS)}`,
    ];
    return values.join(" · ");
  });
};

const formatDelaySummary = (rows: LeonJourneyLogDelayRow[] = []) => {
  if (!rows.length) return ["No delay rows"];
  return rows.map((row) => {
    const values = [
      `${formatNumberLabel(row.minutes)} min`,
      row.reason || "Reason —",
      row.remark || "Remark —",
    ];
    return values.join(" · ");
  });
};

const formatDeicingSummary = (rows: LeonJourneyLogDeicingRow[] = []) => {
  if (!rows.length) return ["No de-icing rows"];
  return rows.map((row) => {
    const values = [
      row.deicing_type || "Type —",
      `Mix ${formatNumberLabel(row.deicing_mix)}`,
      row.deicing_timestamp ? formatDate(row.deicing_timestamp) : "Time —",
      `Qty ${formatNumberLabel(row.qty)}`,
      row.brand || "Brand —",
    ];
    return values.join(" · ");
  });
};

const formatCrewRoleSummary = (journeyLog?: LeonJourneyLogSnapshot | null) => {
  const crewOptions = journeyLog?.crewOptions || [];
  return [
    `PIC: ${formatCrewOptionLabel(crewOptions, journeyLog?.crewRoles?.picCrewId || null)}`,
    `Take-off: ${formatCrewOptionLabel(
      crewOptions,
      journeyLog?.crewRoles?.takeoffCrewId || null,
    )}`,
    `Landing: ${formatCrewOptionLabel(
      crewOptions,
      journeyLog?.crewRoles?.landingCrewId || null,
    )}`,
  ];
};

const scalarHistoryFields: Array<{ key: keyof LeonEditableSnapshot["values"]; label: string }> = [
  { key: "out", label: "OUT" },
  { key: "off", label: "OFF" },
  { key: "on", label: "ON" },
  { key: "in", label: "IN" },
  { key: "uplift", label: "Fuel uplift" },
  { key: "fuelBeforeUplift", label: "Uplift volume (L)" },
  { key: "fuelBeforeUpliftMass", label: "Fuel before uplift" },
  { key: "fuelAfterUpliftMass", label: "Fuel after uplift" },
  { key: "offblockFuel", label: "Offblock fuel" },
  { key: "onblockFuel", label: "Onblock fuel" },
  { key: "density", label: "Density" },
  { key: "burnedFuel", label: "Burned fuel" },
];

const formatScalarHistoryValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" && value.includes("T")) {
    return formatDate(value);
  }
  return `${value}`;
};

const createEmptyHistoryJourneyLog = (): LeonJourneyLogSnapshot => ({
  approaches: [],
  delays: [],
  deicings: [],
  crewOptions: [],
  crewRoles: {
    picCrewId: null,
    takeoffCrewId: null,
    landingCrewId: null,
  },
});

const normalizeHistoryValues = (value?: unknown): LeonEditHistoryValues => {
  const source =
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const nestedValues =
    source.values && typeof source.values === "object"
      ? (source.values as Record<string, unknown>)
      : source;
  const nestedJourneyLog =
    source.journeyLog && typeof source.journeyLog === "object"
      ? (source.journeyLog as Partial<LeonJourneyLogSnapshot>)
      : createEmptyHistoryJourneyLog();

  const values = scalarHistoryFields.reduce((result, { key }) => {
    const nextValue =
      Object.prototype.hasOwnProperty.call(nestedValues, key) &&
      nestedValues[key] !== undefined
        ? (nestedValues[key] as string | number | null)
        : null;
    (result as Record<string, string | number | null>)[key] = nextValue;
    return result;
  }, {} as LeonEditHistoryValues["values"]);

  return {
    values,
    journeyLog: {
      approaches: Array.isArray(nestedJourneyLog.approaches)
        ? nestedJourneyLog.approaches
        : [],
      delays: Array.isArray(nestedJourneyLog.delays) ? nestedJourneyLog.delays : [],
      deicings: Array.isArray(nestedJourneyLog.deicings)
        ? nestedJourneyLog.deicings
        : [],
      crewOptions: Array.isArray(nestedJourneyLog.crewOptions)
        ? nestedJourneyLog.crewOptions
        : [],
      crewRoles: {
        picCrewId: nestedJourneyLog.crewRoles?.picCrewId ?? null,
        takeoffCrewId: nestedJourneyLog.crewRoles?.takeoffCrewId ?? null,
        landingCrewId: nestedJourneyLog.crewRoles?.landingCrewId ?? null,
      },
    },
  };
};

const renderHistoryTextBlock = (title: string, lines: string[]) => (
  <Box
    sx={{
      p: 1.25,
      borderRadius: 2,
      bgcolor: (theme) => theme.palette.action.hover,
      minWidth: 0,
    }}
  >
    <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {lines.map((line, index) => (
        <Typography
          key={`${title}-${index}`}
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}
        >
          {line}
        </Typography>
      ))}
    </Stack>
  </Box>
);

type Props = {
  flightId: string;
  leonTransfer: FlightDetailLeonTransfer;
  onMutated?: () => void;
  onOpenSendOverlay?: () => void;
};

export const FlightLeonTransfer: FunctionComponent<Props> = ({
  flightId,
  leonTransfer,
  onMutated,
  onOpenSendOverlay,
}) => {
  const queryClient = useQueryClient();
  const bgRefreshTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [isTransferring, setIsTransferring] = useState(false);
  const [isRegeneratingPdf, setIsRegeneratingPdf] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [sendVersionId, setSendVersionId] = useState<number | "">("");
  const [sendRecipients, setSendRecipients] = useState("");
  const [sendReason, setSendReason] = useState("");
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [summaryResendSuggestion, setSummaryResendSuggestion] =
    useState<LeonSummaryResendSuggestion | null>(null);
  const [summaryResendError, setSummaryResendError] = useState("");
  const [summaryResendSuccess, setSummaryResendSuccess] = useState("");
  const [isQueuingSummaryResend, setIsQueuingSummaryResend] = useState(false);

  const pdfVersionsWithFile = useMemo(
    () =>
      (leonTransfer.pdfVersions || []).filter(
        (version) =>
          Boolean(version?.filePath) &&
          Boolean(version?.downloadPath || version?.previewPath),
      ),
    [leonTransfer.pdfVersions],
  );

  const preferredSendVersion = useMemo(() => {
    const corrected = pdfVersionsWithFile.find(
      (version) => version.versionType === "corrected" && version.isDefault,
    );
    if (corrected) return corrected;

    const correctedFallback = pdfVersionsWithFile.find(
      (version) => version.versionType === "corrected",
    );
    if (correctedFallback) return correctedFallback;

    const originalDefault = pdfVersionsWithFile.find(
      (version) => version.versionType === "original" && version.isDefault,
    );
    if (originalDefault) return originalDefault;

    return pdfVersionsWithFile[0] || null;
  }, [pdfVersionsWithFile]);

  const selectedSendVersion = useMemo(() => {
    const selected = pdfVersionsWithFile.find(
      (version) => version.id === Number(sendVersionId),
    );
    if (selected) {
      return selected;
    }

    return preferredSendVersion;
  }, [pdfVersionsWithFile, preferredSendVersion, sendVersionId]);

  useEffect(() => {
    if (!sendOpen) return;
    setSendVersionId(preferredSendVersion?.id || "");
    setSendError("");
    setSendSuccess("");
  }, [sendOpen, preferredSendVersion?.id]);

  useEffect(() => {
    return () => {
      bgRefreshTimers.current.forEach(clearTimeout);
      bgRefreshTimers.current = [];
    };
  }, []);

  const scheduleBackgroundRefresh = () => {
    [5000, 15000].forEach((delayMs) => {
      const id = setTimeout(() => {
        queryClient.invalidateQueries(["flight", flightId]);
        queryClient.invalidateQueries(["files", flightId]);
        if (onMutated) {
          onMutated();
        }
      }, delayMs);
      bgRefreshTimers.current.push(id);
    });
  };

  const handleOpenEdit = () => {
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
  };

  const handleEditSaved = ({
    regeneratePdf,
    summaryResendSuggestion: nextSummaryResendSuggestion,
  }: {
    flightId: string;
    regeneratePdf: boolean;
    summaryResendSuggestion?: LeonSummaryResendSuggestion | null;
  }) => {
    queryClient.invalidateQueries(["flight", flightId]);
    queryClient.invalidateQueries(["files", flightId]);
    if (onMutated) {
      onMutated();
    }
    if (regeneratePdf) {
      scheduleBackgroundRefresh();
    }
    if (nextSummaryResendSuggestion?.shouldPrompt) {
      setSummaryResendSuggestion(nextSummaryResendSuggestion);
      setSummaryResendError("");
      setSummaryResendSuccess("");
    }
  };

  const handleCloseSummaryResendPrompt = () => {
    if (isQueuingSummaryResend) {
      return;
    }
    setSummaryResendSuggestion(null);
    setSummaryResendError("");
  };

  const handleQueueSummaryResend = async () => {
    if (!summaryResendSuggestion) {
      return;
    }

    setSummaryResendError("");
    setSummaryResendSuccess("");
    setIsQueuingSummaryResend(true);
    try {
      const response = await fetch(
        `${API_URL}/ops/leon/flights/${summaryResendSuggestion.flightId}/summary-resend`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            versionId: summaryResendSuggestion.correctedPdfVersionId,
          }),
        },
      );

      if (!response.ok) {
        let message = `Failed to queue Summary PDF resend (${response.status})`;
        try {
          const payload = await response.json();
          if (payload?.message) message = payload.message;
          else if (payload?.text) message = payload.text;
        } catch (_error) {
          /* ignore */
        }
        throw new Error(message);
      }

      setSummaryResendSuccess("Updated Summary PDF resend has been queued.");
      setSummaryResendSuggestion(null);
      queryClient.invalidateQueries(["flight", flightId]);
      queryClient.invalidateQueries(["files", flightId]);
      if (onMutated) onMutated();
      scheduleBackgroundRefresh();
    } catch (error) {
      setSummaryResendError(
        error instanceof Error
          ? error.message
          : "Unable to queue updated Summary PDF resend.",
      );
    } finally {
      setIsQueuingSummaryResend(false);
    }
  };

  const handleTransfer = async () => {
    setTransferError("");
    try {
      setIsTransferring(true);
      const response = await fetch(`${API_URL}/ops/leon/flights/${flightId}/transfer`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        let message = `Failed to request LEON transfer (${response.status})`;
        try {
          const payload = await response.json();
          if (payload?.message) message = payload.message;
          else if (payload?.text) message = payload.text;
        } catch (_e) {
          /* ignore */
        }
        setTransferError(message);
        return;
      }

      setConfirmOpen(false);
      queryClient.invalidateQueries(["flight", flightId]);
      invalidateNavigationSummary(queryClient);
      if (onMutated) onMutated();
    } finally {
      setIsTransferring(false);
    }
  };

  const handleRegeneratePdf = async () => {
    setTransferError("");
    setIsRegeneratingPdf(true);
    try {
      const res = await fetch(`${API_URL}/ops/leon/flights/${flightId}/pdf-versions/regenerate`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        let message = `Failed to regenerate PDF (${res.status})`;
        try {
          const payload = await res.json();
          if (payload?.message) message = payload.message;
          else if (payload?.text) message = payload.text;
        } catch (_e) {
          /* ignore */
        }
        throw new Error(message);
      }
      queryClient.invalidateQueries(["flight", flightId]);
      queryClient.invalidateQueries(["files", flightId]);
      if (onMutated) onMutated();
      scheduleBackgroundRefresh();
    } catch (error) {
      setTransferError(
        error instanceof Error
          ? error.message
          : "Unable to regenerate corrected PDF summary.",
      );
    } finally {
      setIsRegeneratingPdf(false);
    }
  };

  const getVersionLabel = (version: LeonPdfVersion) => {
    const typeLabel = version.versionType === "corrected" ? "Corrected" : "Original";
    const defaultLabel = version.isDefault ? " (default)" : "";
    const byLabel = version.editMetadata?.editedBy ? ` by ${version.editMetadata.editedBy}` : "";
    const dateLabel = version.createdAt ? ` · ${formatDate(version.createdAt)}` : "";
    return `${typeLabel}${defaultLabel}${dateLabel}${byLabel}`;
  };

  const getVersionTitle = (version: LeonPdfVersion) =>
    version.label || getFileNameFromPath(version.filePath) || "PDF version";

  const getVersionMeta = (version: LeonPdfVersion) => {
    const details = [version.createdAt ? formatDate(version.createdAt) : null];

    if (version.editMetadata?.editedBy) {
      details.push(`by ${version.editMetadata.editedBy}`);
    }

    const editedFields = version.editMetadata?.editedFields || [];
    if (editedFields.length) {
      details.push(
        `${editedFields.length} change${editedFields.length === 1 ? "" : "s"}`,
      );
    }

    return details.filter(Boolean).join(" · ") || "No metadata";
  };

  const getVersionStatus = (version: LeonPdfVersion) =>
    version.generationStatus ||
    version.editMetadata?.generationStatus ||
    (version.filePath ? "ready" : "processing");

  const getVersionStatusLabel = (version: LeonPdfVersion) => {
    const status = getVersionStatus(version);
    if (status === "failed") return "Failed";
    if (status === "ready") return "Ready";
    return "Processing";
  };

  const getVersionStatusColor = (
    version: LeonPdfVersion,
  ): "success" | "warning" | "error" => {
    const status = getVersionStatus(version);
    if (status === "failed") return "error";
    if (status === "ready") return "success";
    return "warning";
  };

  const getVersionGenerationError = (version: LeonPdfVersion) =>
    version.generationError || version.editMetadata?.generationError || null;

  const handleOpenSendDialog = () => {
    if (onOpenSendOverlay) {
      onOpenSendOverlay();
      return;
    }
    setSendOpen(true);
    setSendError("");
    setSendSuccess("");
  };

  const handleSendSummary = async () => {
    const version = selectedSendVersion;
    if (!version?.filePath) {
      setSendError("Selected PDF version has no file path.");
      return;
    }

    const parsedRecipients = parseRecipients(sendRecipients);
    if (!parsedRecipients.length) {
      setSendError("Enter at least one recipient email.");
      return;
    }

    const invalidRecipients = parsedRecipients.filter((recipient) => !isValidRecipient(recipient));
    if (invalidRecipients.length) {
      setSendError(`Invalid recipients: ${invalidRecipients.join(", ")}`);
      return;
    }

    const summaryFilename =
      getFileNameFromPath(version.filePath) ||
      getFileNameFromPath(version.downloadPath || version.previewPath) ||
      "summary.pdf";

    setSendError("");
    setSendSuccess("");
    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/ops/email-distribution/override`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          flightId,
          recipients: parsedRecipients,
          sendAction: "summary",
          reason:
            sendReason.trim().length > 0
              ? sendReason.trim()
              : "Manual send from flight detail",
          summaryFilePath: version.filePath,
          summaryFilename,
          filePath: version.filePath,
          filename: summaryFilename,
          documentType: "summary",
        }),
      });

      if (!response.ok) {
        let message = `Failed to queue send (${response.status})`;
        try {
          const payload = await response.json();
          if (payload?.message) {
            message = payload.message;
          }
        } catch (_error) {
          // ignore
        }
        setSendError(message);
        return;
      }

      setSendSuccess("Summary PDF queued for distribution.");
      queryClient.invalidateQueries(["ops-email-distribution-flight-details", flightId]);
      if (onMutated) onMutated();
    } finally {
      setIsSending(false);
    }
  };

  const isAmber = leonTransfer.status === "amber";

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          LEON Transfer
        </Typography>

        <Stack spacing={1}>
          {leonTransfer.hasDbState ? (
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                label={isAmber ? "Approval required" : "Transferred"}
                color={isAmber ? "warning" : "success"}
                variant={isAmber ? "outlined" : "filled"}
              />
              <Typography variant="caption" color="text.secondary">
                Mode: {leonTransfer.mode === "hold" ? "Hold" : "Auto + re-transfer"}
              </Typography>
            </Stack>
          ) : null}

          {leonTransfer.hasDbState && leonTransfer.blockerReason ? (
            <Typography variant="body2" color="text.secondary">
              {leonTransfer.blockerReason}
            </Typography>
          ) : null}

          {leonTransfer.lastEdit ? (
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: (theme) => theme.palette.action.hover,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Last edit by <strong>{leonTransfer.lastEdit.actorEmail || "unknown"}</strong>
                {leonTransfer.lastEdit.createdAt
                  ? ` at ${formatDate(leonTransfer.lastEdit.createdAt)}`
                  : ""}
              </Typography>
              {leonTransfer.lastEdit.reason ? (
                <Typography variant="caption" display="block" color="text.secondary">
                  Reason: {leonTransfer.lastEdit.reason}
                </Typography>
              ) : null}
              {leonTransfer.lastEdit.editedFields.length > 0 ? (
                <Typography
                  variant="caption"
                  display="block"
                  color="text.secondary"
                  sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  Fields: {leonTransfer.lastEdit.editedFields.join(", ")}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {leonTransfer.hasDbState ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ pt: 0.5 }}>
              <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={handleOpenEdit}>
                Edit data
              </Button>
              {isAmber ? (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<SendIcon />}
                  disabled={isTransferring}
                  onClick={() => {
                    setTransferError("");
                    setConfirmOpen(true);
                  }}
                >
                  {leonTransfer.mode === "auto_retransfer" ? "Re-transfer" : "Start transfer"}
                </Button>
              ) : null}
              <Button
                size="small"
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleRegeneratePdf}
                disabled={isRegeneratingPdf}
              >
                {isRegeneratingPdf ? "Regenerating..." : "Regenerate PDF"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={handleOpenSendDialog}
                disabled={!leonTransfer.distributionEnabled || (!onOpenSendOverlay && !pdfVersionsWithFile.length)}
              >
                Send files
              </Button>
              <Button
                size="small"
                variant="text"
                component="a"
                href={`/ops/flights/${encodeURIComponent(flightId)}/distribution`}
              >
                Distribution
              </Button>
              <Button
                size="small"
                variant="text"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(true)}
              >
                Edit history
              </Button>
            </Stack>
          ) : null}

          {transferError ? (
            <Typography variant="body2" color="error.main">
              {transferError}
            </Typography>
          ) : null}
          {summaryResendSuccess ? (
            <Typography variant="body2" color="success.main">
              {summaryResendSuccess}
            </Typography>
          ) : null}
          {!leonTransfer.distributionEnabled ? (
            <Typography variant="caption" color="text.secondary">
              Email distribution is not enabled for this operator.
            </Typography>
          ) : null}
        </Stack>

      </Paper>

      <LeonTransferEditDialog
        open={editOpen}
        flightId={flightId}
        flightLabel={flightId}
        editSource="flight_detail"
        onClose={handleCloseEdit}
        onSaved={handleEditSaved}
      />

      <Dialog
        open={Boolean(summaryResendSuggestion)}
        onClose={handleCloseSummaryResendPrompt}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send updated Summary PDF?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2">
              A Summary PDF was already sent for this flight. Send the regenerated
              Summary PDF to the{" "}
              {summaryResendSuggestion?.recipientCount || 0} recipient(s) that
              match the current Summary rules?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              If the corrected PDF is still being generated, the resend stays
              queued and will use that corrected version when it is ready.
            </Typography>
            {summaryResendSuggestion?.recipients?.length ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ overflowWrap: "anywhere" }}
              >
                Recipients: {summaryResendSuggestion.recipients.join(", ")}
              </Typography>
            ) : null}
            {summaryResendError ? (
              <Typography variant="body2" color="error.main">
                {summaryResendError}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSummaryResendPrompt}
            disabled={isQueuingSummaryResend}
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleQueueSummaryResend}
            disabled={isQueuingSummaryResend}
          >
            {isQueuingSummaryResend ? "Queuing..." : "Send when ready"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>LEON history</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                PDF versions
              </Typography>
              {leonTransfer.pdfVersions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No PDF versions recorded.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {leonTransfer.pdfVersions.map((version) => (
                    <Box
                      key={version.id}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            label={version.versionType}
                            color={version.isDefault ? "primary" : "default"}
                            variant={version.isDefault ? "filled" : "outlined"}
                          />
                          <Chip
                            size="small"
                            label={getVersionStatusLabel(version)}
                            color={getVersionStatusColor(version)}
                            variant="outlined"
                          />
                          {version.isDefault ? (
                            <Chip size="small" label="Default" color="primary" variant="outlined" />
                          ) : null}
                        </Stack>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                          >
                            {getVersionTitle(version)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                          >
                            {getVersionMeta(version)}
                          </Typography>
                          {getVersionGenerationError(version) ? (
                            <Typography
                              variant="caption"
                              color="error.main"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              {getVersionGenerationError(version)}
                            </Typography>
                          ) : null}
                        </Box>

                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Button
                            size="small"
                            component="a"
                            href={toAbsoluteApiPath(version.previewPath) || undefined}
                            target="_blank"
                            rel="noreferrer noopener"
                            disabled={!version.previewPath || !version.filePath}
                          >
                            Preview
                          </Button>
                          <Button
                            size="small"
                            component="a"
                            href={toAbsoluteApiPath(version.previewPath || version.downloadPath) || undefined}
                            target="_blank"
                            rel="noreferrer noopener"
                            disabled={(!version.previewPath && !version.downloadPath) || !version.filePath}
                          >
                            Open
                          </Button>
                          <Button
                            size="small"
                            component="a"
                            href={toAbsoluteApiPath(version.downloadPath) || undefined}
                            download={getFileNameFromPath(version.filePath) || undefined}
                            disabled={!version.downloadPath || !version.filePath}
                          >
                            Download
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Edit history
              </Typography>
              {(leonTransfer.editHistory ?? []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No edits recorded.
                </Typography>
              ) : (
                <Stack spacing={2}>
              {(leonTransfer.editHistory ?? []).map((entry) => {
                const oldValues = normalizeHistoryValues(entry.oldValues);
                const newValues = normalizeHistoryValues(entry.newValues);
                const scalarChanges = scalarHistoryFields.filter(({ key }) => {
                  const beforeValue = oldValues.values[key];
                  const afterValue = newValues.values[key];
                  return JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
                });
                const approachChanged =
                  JSON.stringify(oldValues.journeyLog.approaches) !==
                  JSON.stringify(newValues.journeyLog.approaches);
                const delayChanged =
                  JSON.stringify(oldValues.journeyLog.delays) !==
                  JSON.stringify(newValues.journeyLog.delays);
                const deicingChanged =
                  JSON.stringify(oldValues.journeyLog.deicings) !==
                  JSON.stringify(newValues.journeyLog.deicings);
                const crewChanged =
                  JSON.stringify(oldValues.journeyLog.crewRoles) !==
                  JSON.stringify(newValues.journeyLog.crewRoles);

                return (
                  <Box
                    key={entry.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
                          {entry.actorEmail || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(entry.createdAt)} · {entry.editSource || "—"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.75, overflowWrap: "anywhere", wordBreak: "break-word" }}
                        >
                          Reason: {entry.reason || "—"}
                        </Typography>
                        {entry.editedFields.length > 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5, overflowWrap: "anywhere", wordBreak: "break-word" }}
                          >
                            Changed fields: {entry.editedFields.join(", ")}
                          </Typography>
                        ) : null}
                      </Box>

                      {scalarChanges.length ? (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
                            Scalar changes
                          </Typography>
                          <Stack spacing={0.75}>
                            {scalarChanges.map(({ key, label }) => (
                              <Box
                                key={`${entry.id}-${key}`}
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: { xs: "1fr", md: "160px 1fr 1fr" },
                                  gap: 1,
                                  p: 1,
                                  borderRadius: 2,
                                  bgcolor: (theme) => theme.palette.action.hover,
                                }}
                              >
                                <Typography variant="caption" fontWeight={700}>
                                  {label}
                                </Typography>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Before
                                  </Typography>
                                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                                    {formatScalarHistoryValue(oldValues.values[key])}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    After
                                  </Typography>
                                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                                    {formatScalarHistoryValue(newValues.values[key])}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ) : null}

                      {approachChanged ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                          {renderHistoryTextBlock("Approaches before", formatApproachSummary(oldValues.journeyLog.approaches))}
                          {renderHistoryTextBlock("Approaches after", formatApproachSummary(newValues.journeyLog.approaches))}
                        </Box>
                      ) : null}

                      {delayChanged ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                          {renderHistoryTextBlock("Delays before", formatDelaySummary(oldValues.journeyLog.delays))}
                          {renderHistoryTextBlock("Delays after", formatDelaySummary(newValues.journeyLog.delays))}
                        </Box>
                      ) : null}

                      {deicingChanged ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                          {renderHistoryTextBlock("De-icings before", formatDeicingSummary(oldValues.journeyLog.deicings))}
                          {renderHistoryTextBlock("De-icings after", formatDeicingSummary(newValues.journeyLog.deicings))}
                        </Box>
                      ) : null}

                      {crewChanged ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
                          {renderHistoryTextBlock("Crew roles before", formatCrewRoleSummary(oldValues.journeyLog))}
                          {renderHistoryTextBlock("Crew roles after", formatCrewRoleSummary(newValues.journeyLog))}
                        </Box>
                      ) : null}
                    </Stack>
                  </Box>
                );
              })}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send PDF version</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              select
              label="PDF version"
              value={sendVersionId}
              onChange={(event) => setSendVersionId(Number(event.target.value))}
              disabled={!pdfVersionsWithFile.length}
            >
              {pdfVersionsWithFile.map((version) => (
                <MenuItem key={version.id} value={version.id}>
                  {getVersionLabel(version)}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                component="a"
                href={
                  toAbsoluteApiPath(
                    selectedSendVersion?.previewPath || selectedSendVersion?.downloadPath,
                  ) || undefined
                }
                target="_blank"
                rel="noreferrer noopener"
                disabled={!selectedSendVersion}
              >
                Preview selected PDF
              </Button>
            </Stack>
            <TextField
              label="Recipients"
              placeholder="ops@example.com, dispatch@example.com"
              value={sendRecipients}
              onChange={(event) => setSendRecipients(event.target.value)}
              multiline
              minRows={2}
              helperText="Separate multiple recipients by comma or newline."
            />
            <TextField
              label="Reason (optional)"
              value={sendReason}
              onChange={(event) => setSendReason(event.target.value)}
            />
            {sendError ? (
              <Typography variant="body2" color="error.main">
                {sendError}
              </Typography>
            ) : null}
            {sendSuccess ? (
              <Typography variant="body2" color="success.main">
                {sendSuccess}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={isSending}>
            Close
          </Button>
          <Button variant="contained" onClick={handleSendSummary} disabled={isSending || !selectedSendVersion}>
            {isSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm LEON Transfer</DialogTitle>
        <DialogContent>
          <Typography>
            {leonTransfer.mode === "auto_retransfer"
              ? "Are you sure you want to re-transfer this flight to LEON?"
              : "Are you sure you want to start the LEON transfer for this flight?"}
          </Typography>
          {transferError ? (
            <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
              {transferError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setTransferError("");
            }}
            disabled={isTransferring}
          >
            Cancel
          </Button>
          <Button variant="contained" color="success" disabled={isTransferring} onClick={handleTransfer}>
            {isTransferring ? "Sending..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
